import { useEffect, useState, useMemo } from 'react';
import "./AltSlotForm.scss"
import Dropdown from '../../UI/Dropdown/Dropdown';
import { useSelector, useDispatch } from 'react-redux';
import { useFetching } from '../../../hooks/useFetching';
import { useNavigate } from 'react-router-dom';
import PostService from '../../../API/PostService';
import { createTimeSlotsForSpecialist, createTimeSlotsWithHoursSum } from '../../../helpers/table';
import { secondsToTime, getWorkTime, convertDateFormat, timeToSeconds, parseDateToTimestamp } from '../../../helpers/datetime';
import { setSlotsAction } from '../../../store/scheduleReducer';

const AltSlotForm = ({ id = null, closeModal = null, slot = null, date = null, listId = null, specialist = null }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [timeSlots, setTimeSlots] = useState([])
    const [slotsEnd, setSlotsEnd] = useState([])

    const [timeStart, setTimeStart] = useState(null)
    const [timeEnd, setTimeEnd] = useState(null)

    const [config, setConfig] = useState({});
    const [enableFinish, setEnableFinish] = useState(false);

    const [canEdit, setCanEdit] = useState(false);
    const [edited, setEdited] = useState(false);

    const positions = useSelector(state => state.specialists.positions);
    const specialists = useSelector(state => state.schedule.specialists);
    const salons = useSelector(state => state.schedule.salons);
    const slots = useSelector(state => state.schedule.slots);

    useEffect(() => {
        setTimeout(() => {
            setCanEdit(true)
        }, 500);

        setConfig({
            ...config,
            id: id ? id : null,
            worker_id: slot?.worker_id ? slot.worker_id : specialist?.id,
            time_end: slot?.time_end ? slot.time_end : 79200,
            time_start: slot?.time_start ? slot.time_start : 36000,
            site_id: slot?.site_id ? slot?.site_id : null,
            list_id: listId ? listId : null,
            position_id: slot?.position ? slot.position : specialistPositions[0].id,
            date: date ? date : null
        })
    }, [])

    const currentDate = useMemo(() => {
        return date ? date : slot.date_format
    }, [])

    const currentSalon = useMemo(() => {
        return config?.site_id ? salons.find(s => s.id == config.site_id) : null
    }, [config])

    const currentSpecialist = useMemo(() => {
        return specialist ? specialist : specialists.find(s => s.id == slot.worker_id)
    }, [])

    // Получаем список позиций специалиста чтобы можно было выбирать только из тех специальностей которые у него есть
    const specialistPositions = useMemo(() => {
        return positions.filter((p) => currentSpecialist.position_id.includes(p.id))
    }, [currentSpecialist])

    const selectedPositions = useMemo(() => {
        if (!slot) return specialistPositions[0];
        return positions.find((p) => slot.position.includes(p.id))
    }, [currentSpecialist])

    // Получаем список салонов с свободными слотами для текущих специальностей 
    const salonsIds = useMemo(() => {
        const salonsWithPositions = salons.filter(salon => {
            return salon.positions.find(position => position.id == config.position_id);
        });

        return salonsWithPositions;
    }, [config]);

    const busySalons = useMemo(() => {
        const dateTimestamp = slot?.date_timestamp
            ? slot.date_timestamp
            : parseDateToTimestamp(date);

        const matchingSlots = slots.filter((s) => s.date_timestamp === dateTimestamp);

        const workerSlots = matchingSlots.filter(
            (s) => {
                return String(s.position) === String(config.position_id);
            }
        );

        const occupiedIds = new Set(workerSlots.map((s) => String(s.site_id)));

        return salonsIds
            .filter((salon) => occupiedIds.has(String(salon.id)))
            .map((salon) => salon.id);
    }, [config]);

    useEffect(() => {        
        const hoursStart = currentSalon?.settings.time_start
        const hoursEnd = currentSalon?.settings.time_end

        if (hoursStart && hoursStart) {
            setSlotsEnd(createTimeSlotsWithHoursSum(hoursStart, hoursEnd))
            setTimeSlots(createTimeSlotsForSpecialist(hoursStart, hoursEnd))
        } else {
            setSlotsEnd(createTimeSlotsWithHoursSum())
            setTimeSlots(createTimeSlotsForSpecialist(hoursStart, hoursEnd))
        }

        if (slot?.time_start && slot?.time_end) {
            setTimeStart(secondsToTime(slot?.time_start))
            setTimeEnd(getWorkTime(slot?.time_start, slot?.time_end))
        } else if (hoursEnd && hoursStart) {
            setTimeStart(secondsToTime(hoursStart))
            setTimeEnd(getWorkTime(hoursStart, hoursEnd))
        } else {
            setTimeEnd(slotsEnd[slotsEnd.length - 1])
        }
    }, [currentSalon])

    const recieveSalon = (value) => {
        let newConfig = { ...config, site_id: value }
        checkEnableFinish(newConfig, config)
        setConfig(newConfig)
    }

    const recievePosition = (value) => {
        let newConfig = { ...config, position_id: value }
        checkEnableFinish(newConfig, config)
        setConfig(newConfig)
    }

    const saveChanges = () => {
        fetchUpdateSlot(null, config);
    }

    // проверяем форму на валидность
    const checkEnableFinish = (config, oldConfig) => {
        if ((JSON.stringify(config) !== JSON.stringify(oldConfig)) && canEdit) {
            setEdited(true)
        }

        let enable = true;

        if (!config.worker_id) enable = false;

        if (!config.site_id) enable = false;

        if (!config.time_end) enable = false;

        if (!config.time_start) enable = false;

        setEnableFinish(enable)
    }

    const selectedHoursStart = (value) => {
        setTimeStart(value);
        const seconds = timeToSeconds(value);
        let newConfig = { ...config, time_start: seconds };
        checkEnableFinish(newConfig, config);
        setConfig(newConfig);
    };

    const selectedHoursEnd = (value) => {
        const seconds = timeToSeconds(value.time);
        let newConfig = { ...config, time_end: seconds };
        checkEnableFinish(newConfig, config);
        setConfig(newConfig);
    };

    const [fetchSlots] = useFetching(async (listId) => {
        const response = await PostService.getListItems(listId);
        if (response.data.success && response.data.result !== false) {
            dispatch(setSlotsAction(response.data.result[listId]));
        }
    })

    const [fetchUpdateSlot] = useFetching(async (id = null, data = null) => {
        let response;

        if (!data) {
            response = await PostService.deleteSlot(id)
        } else {
            response = await PostService.editSlotItem(data)
        }

        if (response.data.success && response.data.result !== false) {
            fetchSlots(listId)

            navigate('/schedule/');
        }
    })

    const deleteSlot = () => {
        fetchUpdateSlot(id);
    }

    return (
        <div className="slot-form">
            {
                !confirmDelete &&
                <>
                    <div className="slot-form__headline">
                        <h3>{currentSpecialist?.name}, {convertDateFormat(currentDate)}</h3>
                    </div>
                    <div className="slot-form__positions">
                        {/* Если специалист владеет только одной специальностью, то выбор специальностей не доступен */}
                        {specialistPositions.length <= 1 ?
                            <div className="slot-form__positions-item-text">{specialistPositions[0].name}</div>
                            :
                            <Dropdown
                                optionsList={specialistPositions}
                                selectedHandler={recievePosition}
                                label='Специальность'
                                type='positions'
                                optionSelected={selectedPositions ? selectedPositions : specialistPositions[0]}
                            />
                        }
                    </div>
                    <div className="slot-form__clinics">
                        <Dropdown
                            busyList={busySalons}
                            busyText='Недоступен'
                            optionsList={salonsIds}
                            selectedHandler={recieveSalon}
                            label='Салон'
                            type='clinics'
                            optionSelected={currentSalon}
                        />
                    </div>
                    <div className="slot-form__time-block">
                        <div className="slot-form__time-selector">
                            <Dropdown
                                optionsList={timeSlots}
                                optionSelected={timeStart ? timeStart : timeSlots[0]}
                                type='startHours'
                                label=''
                                selectedHandler={selectedHoursStart} />
                        </div>
                        <div className="slot-form-hours__line"></div>
                        <div className="slot-form__time-selector">
                            <Dropdown
                                optionsList={slotsEnd}
                                optionSelected={timeEnd ? timeEnd : slotsEnd[slotsEnd.length - 1]}
                                type='endHours'
                                label=''
                                selectedHandler={selectedHoursEnd} />
                        </div>
                    </div>
                    <div className="slot-form__submit">
                        <>
                            <button className='g-button' disabled={!edited || !enableFinish} onClick={saveChanges}>Сохранить</button>
                            <button className='g-button-secondary' onClick={closeModal}>Отменить</button>
                            {id &&
                                <button className='g-button-link' onClick={() => setConfirmDelete(true)}>Удалить</button>
                            }
                        </>
                    </div>
                </>
            }
            {confirmDelete &&
                <>
                    <div className="record-form__headline">
                        <h3>Подтверждение</h3>
                    </div>
                    <div className="record-form__description">
                        Вы действительно хотите удалить запись?
                    </div>
                    <div className="record-form__btns">
                        <button className='g-button' onClick={deleteSlot}>Удалить</button>
                        <button className='g-button-secondary' onClick={() => setConfirmDelete(false)}>Отменить</button>
                    </div>
                </>
            }
        </div>
    );
};

export default AltSlotForm;
